import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormInstance } from "antd";
import { useMinerContext } from "../../MinerContext";
import useApiRequests from "@services/http/useApiRequest";
import {
  AdminChangeDataSettingsRequest,
  formatMinerMeta,
  IMinerMeta,
} from "@services/changeMinerSettings";
import { makeNotifs } from "@utils/makeNotifs";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { MinerActions } from "../../minerReducer";
import { useContracts } from "@utils/hooks/useContracts";
import { IContractValue } from "@components/global/new/ContractSelector/ContractSelector.props";
import { minerContractService } from "@utils/minerContract";
import { useDispatch } from "react-redux";
import { show } from "redux-modal";
import { MODALS_ENUM } from "@constants/Modals";
import { useIntl } from "react-intl";
import apiService, { createRequestControl } from "@mobx/services/http";
import { SetMinerNotes_RequestTypes } from "@mobx/api/minerNotes";
import { useAuth } from "@utils/hooks/useAuth";
import { setLoading } from "@store/globalNotifications/actions";

const setMinerNote = apiService.createRequest(SetMinerNotes_RequestTypes);
const rc = createRequestControl();

export const useMinerDataFieldsControl = () => {

  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const formRef = useRef<FormInstance<any>>(null);
  const { ok } = makeNotifs();
  const { onSuccess, onLoader } = useGlobalNotifs();
  const { enqueue } = useApiRequests();
  const { miner, dispatchMiner } = useMinerContext();
  const contracts = useContracts();
  const [isEdit, setIsEdit] = useState(false);
  const [ipError, setIpError] = useState(false);
  const [macError, setMacError] = useState(false);
  const [portError, setPortError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [currentContract, setCurrentContract] = useState<
    IContractValue | undefined
  >();

  const auth = useAuth();

  const getDataFromMeta = useCallback(() => {
    const { ip, mac, port, password, ucp, sn } = miner.meta || {};
    return {
      ip,
      mac,
      port: port ?? "",
      password: password ?? "",
      ucp: ucp ?? false,
      sn
    };
  }, [miner.meta]);

  const [ucpError, setUcpError] = useState<string>();
  const [data, setData] = useState<IMinerMeta>(getDataFromMeta());

  const onEdit = () => {
    if (isEdit) {
      onResetForm();
    } else {
      setIsEdit(true);
    }
  };

  const onClearErrors = () => {
    setUcpError(undefined);
    setIpError(false);
    setMacError(false);
    setPortError(false);
    setPasswordError(false);
  };

  const onResetForm = useCallback(() => {
    formRef.current?.resetFields();
    onClearErrors();
    setIsEdit(false);
    setData(getDataFromMeta());
  }, [getDataFromMeta, miner.meta]); // miner.meta - не очевидно, но работает только так

  const requestReset = useCallback(() => {
    const values = formRef.current?.getFieldsValue(true);
    if (values) {

      const keys: (keyof typeof data)[] = [];
      for (const key in values) {
        if (values[key] !== data[key as keyof typeof data]) {
          keys.push(key as keyof typeof data);
        }
      }
      if (keys.length) {
        return dispatch(
          show(MODALS_ENUM.CONFIRM_MODAL, {
            title: formatMessage({
              id: "machines.more.settings.minerData.requestSave.title",
            }),
            subtitle: `${formatMessage({
              id: "machines.more.settings.minerData.requestSave.fields",
            })} ${keys
              .map((key) => {
                if (key === "sn") {
                  return formatMessage({ id: "machines.more.serial.title" });
                } else if (key === "ucp") {
                  return formatMessage({
                    id: "machines.more.settings.password_switch",
                  });
                } else {
                  return formatMessage({ id: `machines.more.settings.${key}` });
                }
              })
              .join(", ")}.`,
            onConfirm: () => onSave(values),
            onCancel: onResetForm,
          })
        );
      }
    }
    onResetForm();
  }, [data]);

  const onCheckData = (value: string, key: keyof IMinerMeta) => {
    switch (key) {
      case "ip":
        if (value.length < 1) {
          setIpError(false);
          return;
        }
        const regex = /^((25[0-5]|(2[0-4]|1\d|[1-9]|)\d)(\.(?!$)|$)){4}$/;
        setIpError(!regex.test(value));
        break;
      case "mac":
        if (value.length < 1) {
          setMacError(false);
          return;
        }
        const r = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
        setMacError(!r.test(value));
        break;
      case "port":
        if (value.length < 1) {
          setPortError(false);
          return;
        }
        break;
      // case "password":
      //   if (value.length < 1) {
      //     setPortError(false)
      //     return
      //   }
      //   break;
      default:
        return false;
    }
  };

  const onSave = useCallback(async (values: IMinerMeta) => {

    if (ipError || macError || portError || passwordError || !isEdit) return;
    if (ucpError !== undefined && ucpError !== "") return;

    console.log("onSave", {
      ipError, macError, portError, passwordError, isEdit, ucpError
    });

    const params = {
      ...miner.meta,
      ...values,
      password: values.ucp ? "" : values.password,
    };
    const res = await enqueue(
      new AdminChangeDataSettingsRequest(miner.id, params),
      {
        onLoadingStatusChanged: setSaveLoading,
      }
    );
    if (!res) return;
    onSuccess(ok.success);
    setIsEdit(false);
    onClearErrors();
    dispatchMiner({ type: MinerActions.Meta, payload: params });
  }, [ipError, macError, portError, passwordError, isEdit, ucpError, dispatchMiner]);

  const onChangeContractPass = useCallback((t: boolean) => {
    console.log("onChangeContractPass", {
      t,
      contract: miner.contract_id
    });
    if (t && (miner.contract_id === undefined || miner.contract_id === null || miner.contract_id === 0)) {
      setData(prev => ({
        ...prev,
        ucp: false
      }));
      setUcpError(formatMessage({ id: "machines.more.settings.data_fields.ucp_no_contract_error" }));
      return;
    }
    setData(prev => ({
      ...prev,
      ucp: t
    }));
  }, [miner.contract_id, setData]);

  useEffect(() => {
    const crCon = contracts?.find((c) => c.id === miner.contract_id);
    minerContractService.setMinerContract(miner.id, crCon);
    setCurrentContract(crCon);
  }, [miner]);


  const addendToComment = useCallback(async () => {
    if (!miner.meta) return;
    const newNotes = `${miner.admin_note}\n----\n${formatMinerMeta(miner.meta)}`;
    onLoader(true);
    try {
      const r = await setMinerNote(rc.createRequestHandle(), miner.id, newNotes);
      if (r && r.Status === "success") {
        dispatchMiner({
          type: MinerActions.AppendMetaToAdminNotes,
          payload: newNotes,
        });
        onSuccess(ok.success);
      }
    } finally {
      setLoading(false);
    }
  }, [miner, miner.meta, dispatchMiner])

  return {
    isAdmin: auth.isAdmin,
    formRef,
    isEdit,
    ipError,
    macError,
    portError,
    passwordError,
    data,
    saveLoading,
    //isContractPass,
    currentContract,
    ucpError,
    addendToComment,
    formatMessage,
    onEdit,
    onSave,
    onCheckData,
    onResetForm,
    onChangeContractPass,
    requestReset,
  };
};
